// The mapping panel: each distinct Asana value gets its Spark value once, and every line that
// carries it follows. Rows read Asana's value and how many carry it, then the Spark control.

import React, { useMemo, useState } from 'react';
import { Checkbox } from '../components/ui/Checkbox';
import { Chip, ChipGroup } from '../components/ui/Chip';
import { Panel } from '../components/ui/Panel';
import Reveal from '../components/ui/Reveal';
import { Select } from '../components/ui/Select';
import type { SelectOption } from '../components/ui/Select';
import type { AsanaPreview } from '../Spark/sparkApi';
import { PIPELINE, SPECIALITIES, SPICE, STATUS_REASONS, STUDIO_ROLES, WORK_STEPS } from '../Spark/sparkRules';
import type { Speciality, Stage } from '../Spark/sparkRules';
import { BLANK, fromTokens, repeatedNames } from './asanaImportModel';
import type { EffectiveMapping } from './asanaImportModel';
import type { MappingChoices, PickPerson, TokenMap } from './asanaImportTypes';

const PICK = 'pick';
const NONE = 'none';

const STAGE_OPTIONS: SelectOption[] = [
  { key: PICK, label: 'Pick on each project' },
  ...PIPELINE.map((p) => ({ key: p.key, label: p.label, hex: p.color })),
];
const REASON_OPTIONS: SelectOption[] = [
  { key: NONE, label: 'No status' },
  ...STATUS_REASONS.map((s) => ({ key: s.label, label: s.label, hex: s.color })),
];
const SPICE_OPTIONS: SelectOption[] = [
  { key: PICK, label: 'Pick on each project' },
  ...SPICE.map((s) => ({ key: String(s.level), label: s.label, hint: s.short })),
];
const TOKEN_OPTIONS: SelectOption[] = [
  ...SPECIALITIES.map((s) => ({ key: `speciality:${s.key}`, label: s.label, group: 'Speciality' })),
  { key: 'steps', label: 'Adds work steps' },
  { key: 'ignore', label: 'Ignore' },
];
const FALLBACK_OPTIONS: SelectOption[] = [
  { key: PICK, label: 'Pick on each project' },
  ...SPECIALITIES.map((s) => ({ key: s.key, label: s.label })),
];

const tokenKey = (m: TokenMap): string => (m.kind === 'speciality' ? `speciality:${m.speciality}` : m.kind);
const shown = (value: string) => (value === BLANK ? 'Blank in Asana' : value);
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

const roleWords = (p: PickPerson): string | undefined => {
  if (!p.active) return 'Deactivated';
  if (!p.inSpark) return 'Cannot open Spark';
  const words = [
    p.producer && 'Producer',
    p.pilot && 'Pilot',
    ...p.roles.map((r) => STUDIO_ROLES.find((s) => s.key === r)?.label).filter(Boolean),
  ].filter(Boolean);
  return words.length ? words.join(', ') : p.studio ? 'Studio' : undefined;
};

let seq = 0;

const Block: React.FC<{ title: string; hint: string; children: React.ReactNode; hintId?: string }> = ({ title, hint, children, hintId }) => {
  const [id] = useState(() => `asana-map-${++seq}`);
  return (
    <section aria-labelledby={id} className="min-w-0">
      <h3 id={id} className="m-0 text-sm font-semibold leading-5 text-on-surface">{title}</h3>
      <p id={hintId} className="m-0 mt-0.5 max-w-[65ch] text-xs leading-4 text-on-surface-variant">{hint}</p>
      <ul className="m-0 mt-2.5 grid list-none gap-1 p-0">{children}</ul>
    </section>
  );
};

/** One Asana value: what it is and how many carry it, then its Spark control. */
const MapRow: React.FC<{ value: React.ReactNode; sub?: React.ReactNode; count: React.ReactNode; children: React.ReactNode; below?: React.ReactNode }> = ({
  value, sub, count, children, below,
}) => (
  <li className="min-w-0 rounded-control bg-surface-container px-3 py-1.5">
    <div className="grid min-w-0 items-center gap-x-3 gap-y-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,15rem)]">
      <div className="flex min-w-0 items-center gap-2">
        <div className="min-w-0 flex-1">
          <span className="block truncate text-sm leading-5 text-on-surface">{value}</span>
          {sub && <span className="block truncate text-xs leading-4 text-on-surface-variant">{sub}</span>}
        </div>
        <span className="flex-none text-xs tabular-nums text-on-surface-variant">{count}</span>
      </div>
      <div className="min-w-0">{children}</div>
    </div>
    {below}
  </li>
);

export interface AsanaImportMappingProps {
  preview: AsanaPreview;
  eff: EffectiveMapping;
  people: PickPerson[];
  onMapping: (change: (m: MappingChoices) => MappingChoices) => void;
  open: boolean;
  onToggle: () => void;
}

const AsanaImportMapping: React.FC<AsanaImportMappingProps> = ({ preview, eff, people, onMapping, open, onToggle }) => {
  const { values } = preview;
  const repeats = useMemo(() => repeatedNames(preview.lines), [preview]);
  // Counted live: a token switched to a speciality takes its lines out of this row.
  const unnamed = useMemo(
    () => preview.lines.filter((l) => fromTokens(l, eff).specialities.length === 0).length,
    [preview, eff],
  );
  const personOptions: SelectOption[] = useMemo(() => [
    { key: NONE, label: 'Nobody' },
    ...people.map((p) => ({ key: p.id, label: p.name, hint: roleWords(p) })),
  ], [people]);

  const setStage = (value: string, to: Stage | null) => onMapping((m) => ({ ...m, pipeline: { ...m.pipeline, [value]: to } }));
  const setReason = (value: string, to: string | null) => onMapping((m) => ({ ...m, status: { ...m.status, [value]: to } }));
  const setSpice = (value: string, to: number | null) => onMapping((m) => ({ ...m, spice: { ...m.spice, [value]: to } }));
  const setPerson = (key: string, to: string | null) => onMapping((m) => ({ ...m, people: { ...m.people, [key]: to } }));
  const setTokenMap = (token: string, to: TokenMap) => onMapping((m) => ({ ...m, speciality: { ...m.speciality, [token]: to } }));

  const setToken = (token: string, key: string) => {
    const current = eff.speciality[token];
    const next: TokenMap = key.startsWith('speciality:')
      ? { kind: 'speciality', speciality: key.slice('speciality:'.length) as Speciality }
      : key === 'steps'
        ? { kind: 'steps', steps: current && current.kind === 'steps' ? current.steps : [] }
        : { kind: 'ignore' };
    setTokenMap(token, next);
  };
  const toggleStep = (token: string, step: string) => {
    const current = eff.speciality[token];
    const steps = current && current.kind === 'steps' ? current.steps : [];
    setTokenMap(token, { kind: 'steps', steps: steps.includes(step) ? steps.filter((s) => s !== step) : [...steps, step] });
  };
  const toggleLeaveOut = (name: string, on: boolean) =>
    onMapping((m) => ({ ...m, leaveOut: on ? Array.from(new Set([...m.leaveOut, name])) : m.leaveOut.filter((n) => n !== name) }));

  const tokens = values.speciality.filter((v) => v.value !== BLANK);
  const repeatsHint = 'asana-map-repeats-hint';

  return (
    <Panel title="Map once; every project follows" collapse={{ open, onToggle }}>
      <p className="m-0 mb-4 max-w-[65ch] text-sm leading-5 text-on-surface-variant">
        Each Asana value gets its Spark value here, and every project carrying it follows. A change made on one project
        wins over the mapping for that project.
      </p>
      <div className="grid gap-x-8 gap-y-6 xl:grid-cols-2">
        <Block title="Asana pipeline to Spark pipeline" hint="Every project needs a pipeline column. Pick on each project leaves it to you line by line.">
          {values.pipeline.map((v) => (
            <MapRow key={v.value} value={shown(v.value)} count={plural(v.count, 'project')}>
              <Select
                size="sm"
                label={`Pipeline for ${shown(v.value)}`}
                options={STAGE_OPTIONS}
                value={eff.pipeline[v.value] || PICK}
                onChange={(k) => setStage(v.value, k === PICK ? null : (k as Stage))}
              />
            </MapRow>
          ))}
        </Block>

        <Block title="Asana status to Spark status" hint="Spark's statuses. An Asana status with no match comes in with no status.">
          {values.status.map((v) => (
            <MapRow key={v.value} value={shown(v.value)} count={plural(v.count, 'project')}>
              <Select
                size="sm"
                label={`Status for ${shown(v.value)}`}
                options={REASON_OPTIONS}
                value={eff.status[v.value] || NONE}
                onChange={(k) => setReason(v.value, k === NONE ? null : k)}
              />
            </MapRow>
          ))}
        </Block>

        <Block title="Spice" hint="Every project needs a spice level.">
          {values.spice.map((v) => (
            <MapRow key={v.value} value={shown(v.value)} count={plural(v.count, 'project')}>
              <Select
                size="sm"
                label={`Spice for ${shown(v.value)}`}
                options={SPICE_OPTIONS}
                value={eff.spice[v.value] ? String(eff.spice[v.value]) : PICK}
                onChange={(k) => setSpice(v.value, k === PICK ? null : Number(k))}
              />
            </MapRow>
          ))}
        </Block>

        <Block
          title="Speciality"
          hint="Asana lists several per project. Each names a Spark speciality, adds work steps, or is ignored. Two different specialities on one project ask you to pick."
        >
          {tokens.map((v) => {
            const m: TokenMap = eff.speciality[v.value] || { kind: 'ignore' };
            const steps = m.kind === 'steps' ? m.steps : [];
            return (
              <MapRow
                key={v.value}
                value={v.value}
                count={plural(v.count, 'project')}
                below={(
                  <Reveal show={m.kind === 'steps'}>
                    <ChipGroup label={`Work steps ${v.value} adds`} className="pb-1 pt-2">
                      {WORK_STEPS.map((w) => (
                        <Chip key={w} pressed={steps.includes(w)} onClick={() => toggleStep(v.value, w)}>{w}</Chip>
                      ))}
                    </ChipGroup>
                  </Reveal>
                )}
              >
                <Select size="sm" label={`Spark meaning of ${v.value}`} options={TOKEN_OPTIONS} value={tokenKey(m)} onChange={(k) => setToken(v.value, k)} />
              </MapRow>
            );
          })}
          <MapRow value="Names no speciality" sub="Blank in Asana, or only work steps and ignored values" count={plural(unnamed, 'project')}>
            <Select
              size="sm"
              label="Speciality when Asana names none"
              options={FALLBACK_OPTIONS}
              value={eff.noSpeciality || PICK}
              onChange={(k) => onMapping((m) => ({ ...m, noSpeciality: k === PICK ? null : (k as Speciality) }))}
            />
          </MapRow>
        </Block>

        <Block title="People" hint="Each Asana person, matched by email. The count includes subtasks they were assigned.">
          {values.people.map((p) => (
            <MapRow
              key={p.key}
              value={p.name || p.email || p.key}
              sub={!p.user
                ? (p.email ? `${p.email}, no Firefleet account` : 'No email in Asana')
                : !p.user.active ? `${p.email}, deactivated in Firefleet` : p.email}
              count={plural(p.count, 'task')}
            >
              <Select
                size="sm"
                label={`Firefleet person for ${p.name || p.key}`}
                options={personOptions}
                value={eff.people[p.key] || NONE}
                onChange={(k) => setPerson(p.key, k === NONE ? null : k)}
              />
            </MapRow>
          ))}
          {values.people.length === 0 && (
            <li className="text-sm text-on-surface-variant">Nobody is assigned anywhere in this export.</li>
          )}
        </Block>

        {repeats.length > 0 && (
          <Block
            title="Subtasks that repeat"
            hint="Ticked names are left out of every project. One project can still keep one."
            hintId={repeatsHint}
          >
            {repeats.map((r) => (
              <li key={r.name} className="flex min-w-0 items-center gap-3 rounded-control bg-surface-container px-3 py-2">
                <Checkbox
                  fill
                  label={r.name}
                  checked={eff.leaveOut.has(r.name)}
                  aria-describedby={repeatsHint}
                  onChange={(e) => toggleLeaveOut(r.name, e.target.checked)}
                  className="min-w-0 flex-1"
                />
                <span className="flex-none text-xs tabular-nums text-on-surface-variant">{plural(r.count, 'time')}</span>
              </li>
            ))}
          </Block>
        )}
      </div>
    </Panel>
  );
};

export default AsanaImportMapping;
